import { EventrDate, IEventrActivity } from '@eventr/common'
import { ColoursObj, Icon } from '@eventr/common-storybook'
import { Key, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { DisclosureGroup } from '../../../common/DisclosureGroup'
import i18n from '../../../i18n/i18next'
import { bs, useBoundStore } from '../../../state/bound.store'
import {
    selectAllPublishedTodos,
    selectTodoListRange,
} from '../../../state/itineraryState/currentItin.slice'
import { useScreenTypeContext } from '../../state/screenTypeProvider'
import {
    useTotalAllPendingTodoCount,
    useTotalPendingTodoCount,
} from '../hooks/usePendingTodoCount'
import { TodoCard, TodoRange } from '../todoCard'

interface TodoDisclosureGroupProps {
    open?: boolean
    /**
     * use this prop to pass in todos from the parent component
     */
    todosList?: IEventrActivity[]
}

export const TodoDisclosureGroup = ({
    open = true,
    todosList,
}: TodoDisclosureGroupProps) => {
    const { screenType, stickyHeaders } = useScreenTypeContext()
    const { todos } = useBoundStore(
        useShallow((s) => {
            return {
                todos: s.itineraryComponents.todos,
            }
        })
    )
    const allTodos = useBoundStore(selectAllPublishedTodos)
    const allTodosList = todosList ?? allTodos
    const totalPending = useTotalPendingTodoCount()
    const allPending = useTotalAllPendingTodoCount(todosList)

    const orderedTodos = useMemo(() => {
        if (screenType === 'team') {
            return allTodosList.sort((a: any, b: any) => {
                const aDate = a.due_date
                    ? new EventrDate(a.due_date).getUnixMs
                    : Number.MAX_VALUE
                const bDate = b.due_date
                    ? new EventrDate(b.due_date).getUnixMs
                    : Number.MAX_VALUE
                return aDate - bDate
            })
        }
        return todos.sort((a, b) => {
            const aDate = a.due_date
                ? new EventrDate(a.due_date).getUnixMs
                : Number.MAX_VALUE
            const bDate = b.due_date
                ? new EventrDate(b.due_date).getUnixMs
                : Number.MAX_VALUE
            return aDate - bDate
        })
    }, [todos, screenType, allTodosList])

    const range: TodoRange = useMemo(() => {
        if (orderedTodos.length === 0) return 'normal'
        if (screenType === 'edit') return 'normal'
        if (orderedTodos.length === 0) return 'done'
        return selectTodoListRange(bs(), orderedTodos)
    }, [orderedTodos, screenType])
    const countBgColor = useMemo(() => {
        switch (range) {
            case 'danger':
                return ColoursObj.states.error
            case 'warning':
                return ColoursObj.states.warning
            default:
                return undefined
        }
    }, [range])

    if (screenType === 'upNext') {
        if (totalPending === 0) return null
    }
    if (screenType === 'team') {
        if (allTodosList.length === 0) return null
    }
    if (screenType === 'personal') {
        if (todos.length === 0) return null
    }
    return (
        <DisclosureGroup
            stickyHeader={stickyHeaders}
            containerStyle={{
                backgroundColor: ColoursObj.backgrounds.primary,
            }}
            id='todo'
            title={i18n.t('sections.toDo')}
            countBgColor={countBgColor}
            isOpen={open}
            count={screenType === 'team' ? allPending : totalPending}
            icon={
                <Icon
                    name="ToDo"
                    iconColour={ColoursObj.backgrounds.primary}
                    bubble
                />
            }
            content={orderedTodos.map(
                (todo: IEventrActivity, index: Key | null | undefined) => {
                    return <TodoCard id={'todo'} key={index} todo={todo} />
                }
            )}
        />
    )
}
